import { useAtomValue, useSetAtom } from "jotai";
import { paymentChannelAtom, userAtom } from "../utils/store";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getPaymentChannels,
  processPaymentWithChannel,
} from "../utils/request";
import { useCallback } from "react";
import { INTERSWITCH_PAYMENT_URL } from "../utils/constants";
import { App } from "antd";

interface ProcessedPaymentResult {
  channel: string;
  paymentResponse: PaymentResponse;
  redirectPayload: RedirectData;
}

export const useInitiatePayment = () => {
  const { notification } = App.useApp();
  const user = useAtomValue(userAtom);
  const setPaymentChannel = useSetAtom(paymentChannelAtom);

  const paymentChannelsQuery = useQuery({
    queryKey: ["paymentChannels"],
    queryFn: getPaymentChannels,
    enabled: true, // ✅ always fetch
  });
  const processPaymentMutation = useMutation({
    mutationFn: (payload: PaymentPayloadWithChannel) =>
      processPaymentWithChannel(payload),
  });

  const submitRedirectForm = useCallback(
    (
      action: string,
      fields: Record<string, string | number | null | undefined>,
    ) => {
      const form = document.createElement("form");
      form.method = "POST";
      form.action = action;
      form.style.display = "none";

      Object.entries(fields).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
          return;
        }

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    },
    [],
  );

  const makePayment = useCallback(
    async (
      payload: PaymentPayloadWithChannel,
    ): Promise<ProcessedPaymentResult> => {
      setPaymentChannel(payload.channel);
      try {
        let payloadToSend: ProcessPaymentPayloadWithChannel = {
          amount: payload.amount,
          returnUrl: payload.returnUrl,
          applicantId: user?.applicantId ?? "",
          paymentType: payload.paymentType,
        };

        if (payload.couponCode) {
          payloadToSend.couponCode = payload.couponCode;
        }

        const response = await processPaymentMutation.mutateAsync({
          ...payloadToSend,
          channel: payload.channel,
        });

        const paymentResponse = response.data as PaymentResponse;

        const redirectPayload = JSON.parse(
          paymentResponse.responsePayLoad,
        ) as RedirectData;

        const channel = payload.channel?.trim().toLowerCase();

        // 🟢 Interswitch
        if (channel === "interswitch") {
          const interswitchData =
            redirectPayload?.Data as InterswitchRedirectData;

          submitRedirectForm(INTERSWITCH_PAYMENT_URL, { ...interswitchData });
          return {
            channel,
            paymentResponse,
            redirectPayload,
          };
        }

        if (channel === "remita") {
          return {
            channel,
            paymentResponse,
            redirectPayload,
          };
        }

        const redirectUrl = redirectPayload?.RedirectURL;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }

        return {
          channel,
          paymentResponse,
          redirectPayload,
        };
      } catch (error: any) {
        notification.error({
          message: "Payment Error",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "Unable to initiate payment.",
        });
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      notification,
      processPaymentMutation,
      setPaymentChannel,
      submitRedirectForm,
      user?.applicantId,
    ],
  );

  return {
    makePayment,
    isLoading: processPaymentMutation.isPending,
    paymentChannels: paymentChannelsQuery.data?.data as PaymentChannel[],
    isLoadingChannels: paymentChannelsQuery.isLoading,
    refetchChannels: paymentChannelsQuery.refetch,
  };
};
